import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Search } from 'lucide-react';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

export default function MasterData() {
  const { isEditor, canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState('batches');
  const [acctCats, setAcctCats]     = useState([]);
  const [acctParties, setAcctParties] = useState([]);
  const [acctPayModes, setAcctPayModes] = useState([]);
  const [acctCatForm, setAcctCatForm] = useState({name:'',type:'in',category_type:'General'});
  const [editingCat, setEditingCat] = useState(null);
  const [editingParty, setEditingParty] = useState(null);
  const [editingPayMode, setEditingPayMode] = useState(null);
  const [acctPartyForm, setAcctPartyForm] = useState({party_name:'',party_type:'customer',contact:''});
  const [acctPayForm, setAcctPayForm] = useState({name:''});
  const [acctSearch, setAcctSearch] = useState('');
  const [selectedAcct, setSelectedAcct] = useState(new Set());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const [batches, setBatches] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCoordHistory, setShowCoordHistory] = useState(null);
  const [coordHistory, setCoordHistory] = useState([]);

  const [facultyForm, setFacultyForm] = useState({
    faculty_name: '', specialization: '', qualification: '',
    contact_number: '', email: '', is_active: true
  });

  // Batch form — no course_package_id
  const [batchForm, setBatchForm] = useState({
    batch_name: '', discipline: 'mechanical',
    start_date: '', end_date: '',
    handling_staff_id: '', max_capacity: 50, status: 'upcoming'
  });

  const [collegeForm, setCollegeForm] = useState({
    college_name: '', university: '', district: '', current_status: 'Active',
    state: '', contact_person: '', contact_number: '', email: '', college_short_code: ''
  });

  // Course form — renamed course_fees → tuition_fees, added num_ndt_certificates, num_nactet_certificates
  const [topicSearch, setTopicSearch] = useState('');
  const [topicDisciplineFilter, setTopicDisciplineFilter] = useState('');
  const [courseForm, setCourseForm] = useState({
    course_name: '', course_short_code: '', discipline: 'mechanical',
    duration_value: '', duration_unit: 'months',
    tuition_fees: '',
    topic_total_fees: 0,
    discount: '',
    university_board: '',
    selected_topics: [],
    num_ndt_certificates: 0,
    num_nactet_certificates: 0,
    num_experience_certificates: 0,
    ndt_cutoff_marks: 60,
    nactet_cutoff_marks: 50
  });

  // Topic form
  const [topicForm, setTopicForm] = useState({
    topic_name: '', short_code: '', duration_days: '', fees: '', discipline: 'mechanical'
  });

  useEffect(() => { loadAll(); loadAcctMaster(); }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadAll(); } catch(e){} }, 800); };
    const ch = supabase.channel('masterdata-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'batches'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'colleges'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);


  async function loadAcctMaster() {
    const [cats,parties,modes] = await Promise.all([
      supabase.from('accounts_categories').select('*').order('name'),
      supabase.from('accounts_parties').select('*').order('party_name'),
      supabase.from('accounts_payment_modes').select('*').order('name'),
    ]);
    if (cats.error || parties.error || modes.error) toast.error('Could not load accounts master data: ' + (cats.error || parties.error || modes.error).message);
    setAcctCats(cats.data||[]);
    setAcctParties(parties.data||[]);
    setAcctPayModes(modes.data||[]);
  }

  async function loadAll() {
    setLoading(true);
    const [b, c, cp, s, t, f] = await Promise.all([
      supabase.from('batches').select('*, staff(full_name)').order('created_at', { ascending: false }),
      supabase.from('colleges').select('*').order('college_name'),
      supabase.from('course_packages').select('*').order('course_name'),
      supabase.from('staff').select('id, full_name').eq('is_active', true),
      supabase.from('topics').select('*').order('topic_name'),
      supabase.from('faculties').select('*').order('faculty_name'),
    ]);
    const lerr = [b, c, cp, s, t, f].find(r => r.error);
    if (lerr) toast.error('Could not load master data: ' + lerr.error.message);
    setBatches(sortBatches(b.data || []));
    setColleges(c.data || []);
    setCourses(cp.data || []);
    setStaff(s.data || []);
    setTopics(t.data || []);
    setFaculties(f.data || []);
    setLoading(false);
  }

  // ── SAVE BATCH ──
  async function saveBatch() {
    if (!batchForm.batch_name.trim()) { toast.error('Batch name is required'); return; }
    setSaving(true);

    const payload = {
      batch_name:   batchForm.batch_name.trim(),
      discipline:   batchForm.discipline || 'mechanical',
      centre:       batchForm.centre || 'EKM',
      max_capacity: Number(batchForm.max_capacity) || 50,
      remarks:      batchForm.remarks || '',
      status:       batchForm.status || 'upcoming',
    };
    if (batchForm.handling_staff_id) payload.handling_staff_id = batchForm.handling_staff_id;
    if (batchForm.start_date)        payload.start_date = batchForm.start_date;
    if (batchForm.end_date)          payload.end_date   = batchForm.end_date;
    if (!editing) {
      payload.batch_code = `BATCH-${Date.now().toString().slice(-6)}`;
    } else {
      // Don't send batch_code on update — it already exists
      delete payload.batch_code;
    }
    const { error } = editing
      ? await supabase.from('batches').update(payload).eq('id', editing)
      : await supabase.from('batches').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Batch saved'); setShowModal(null); loadAll(); }
    setSaving(false);
  }

  // ── SAVE COLLEGE ──
  async function saveCollege() {
    if (!collegeForm.college_name.trim()) { toast.error('College name is required'); return; }
    setSaving(true);
    const payload = {
      college_name:       collegeForm.college_name.trim(),
        current_status:     collegeForm.current_status || 'Active',
        zone:               collegeForm.zone || '',
      university:         collegeForm.university    || null,
      district:           collegeForm.district      || null,
      state:              collegeForm.state         || null,
      contact_person:     collegeForm.contact_person || null,
      contact_number:     collegeForm.contact_number || null,
      email:              collegeForm.email         || null,
      college_short_code: collegeForm.college_short_code || null,
    };
    if (!editing) {
      payload.college_code = `COL-${Date.now().toString().slice(-6)}`;
    } else {
      delete payload.college_code;
    }
    const { error } = editing
      ? await supabase.from('colleges').update(payload).eq('id', editing)
      : await supabase.from('colleges').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('College saved'); setShowModal(null); loadAll(); }
    setSaving(false);
  }

  // ── SAVE COURSE ──
  async function saveCourse() {
    if (!courseForm.course_name.trim()) { toast.error('Course name is required'); return; }
    setSaving(true);

    // For new records generate a unique code
    // For edits, fetch the existing code from the courses array
    let course_code;
    if (editing) {
      const existing = courses.find(c => c.id === editing);
      course_code = existing?.course_code || `COURSE-${Date.now().toString().slice(-6)}`;
    } else {
      const prefix = courseForm.discipline === 'civil' ? 'CIV' : 'MECH';
      course_code = `${prefix}-${Date.now().toString().slice(-5)}`;
    }

    // Build EXPLICIT payload — no spread to avoid sending empty strings into numeric columns
    const tuition   = Number(courseForm.tuition_fees)   || 0;
    const dur       = Number(courseForm.duration_value)  || 0;
    const ndtCerts  = Number(courseForm.num_ndt_certificates)    || 0;
    const nacCerts  = Number(courseForm.num_nactet_certificates)  || 0;
    const expCerts  = Number(courseForm.num_experience_certificates) || 0;
    const ndtCut    = Number(courseForm.ndt_cutoff_marks)    || 0;
    const nacCut    = Number(courseForm.nactet_cutoff_marks) || 0;

    const payload = {
      course_code,
      course_name:          courseForm.course_name.trim(),
      course_short_code:    (courseForm.course_short_code||'').trim().toUpperCase() || null,
      discipline:           courseForm.discipline || 'mechanical',
      duration_value:       dur,
      duration_unit:        courseForm.duration_unit || 'months',
      course_fees:          tuition,
      tuition_fees:         tuition,
      university_board:     courseForm.university_board || null,
      selected_topics:      courseForm.selected_topics || [],
      num_certificates:     ndtCerts + nacCerts,
      num_ndt_certificates: ndtCerts,
      num_nactet_certificates: nacCerts,
      num_experience_certificates: expCerts,
      ndt_cutoff_marks:     ndtCut,
      nactet_cutoff_marks:  nacCut,
      is_active:            true,
    };
    const { data: savedCourse, error } = editing
      ? await supabase.from('course_packages').update(payload).eq('id', editing).select().single()
      : await supabase.from('course_packages').insert(payload).select().single();

    if (error) { toast.error(error.message); setSaving(false); return; }

    // Auto-update tuition fees in all students enrolled in this course package
    if (savedCourse?.id) {
      const { error: feeErr } = await supabase.from('students')
        .update({ fee_tuition: Number(courseForm.tuition_fees || 0) })
        .eq('course_package_id', savedCourse.id);
      if (feeErr) toast.error('Course saved, but student fees were not updated: ' + feeErr.message);
    }

    if (!(savedCourse?.id)) toast.success('Course saved'); else toast.success('Course saved & tuition fees updated in student profiles');
    setShowModal(null);
    loadAll();
    setSaving(false);
  }

  // ── SAVE TOPIC ──
  async function saveTopic() {
    if (!topicForm.topic_name.trim()) { toast.error('Topic name is required'); return; }
    setSaving(true);
    const payload = {
      topic_name:    topicForm.topic_name,
      short_code:    topicForm.short_code || null,
      is_lab:        topicForm.is_lab || false,
      duration_days: Number(topicForm.duration_days || 0),
      fees:          topicForm.fees ? Number(topicForm.fees) : null,
      discipline:    topicForm.discipline || 'mechanical',
    };
    const { error } = editing
      ? await supabase.from('topics').update(payload).eq('id', editing)
      : await supabase.from('topics').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Topic saved'); setShowModal(null); loadAll(); }
    setSaving(false);
  }

  // ── SAVE FACULTY ──
  async function saveFaculty() {
    if (!facultyForm.faculty_name.trim()) { toast.error('Faculty name is required'); return; }
    setSaving(true);
    const payload = {
      faculty_name:    facultyForm.faculty_name.trim(),
      specialization:  facultyForm.specialization || null,
      qualification:   facultyForm.qualification || null,
      contact_number:  facultyForm.contact_number || null,
      email:           facultyForm.email || null,
      is_active:       facultyForm.is_active,
    };
    const { error } = editing
      ? await supabase.from('faculties').update(payload).eq('id', editing)
      : await supabase.from('faculties').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Faculty saved'); setShowModal(null); loadAll(); }
    setSaving(false);
  }

  // ── OPEN EDIT ──
  function openEdit(type, item) {
    setEditing(item.id);
    if (type === 'batch') {
      setBatchForm({
        batch_name: item.batch_name || '',
        discipline: item.discipline || 'mechanical',
        centre:     item.centre || 'EKM',
        remarks:    item.remarks || '',
        start_date: item.start_date || '',
        end_date: item.end_date || '',
        handling_staff_id: item.handling_staff_id || '',
        max_capacity: item.max_capacity || 50,
        status: item.status || 'upcoming'
      });
    } else if (type === 'college') {
      setCollegeForm({
        college_name: item.college_name || '', university: item.university || '',
        district: item.district || '', state: item.state || '',
        contact_person: item.contact_person || '', contact_number: item.contact_number || '',
        email: item.email || '', college_short_code: item.college_short_code || '', current_status: item.current_status || 'Active', zone: item.zone || '',
      });
    } else if (type === 'course') {
      setCourseForm({
        course_name:          item.course_name || '',
        course_short_code:    item.course_short_code || '',
        discipline:           item.discipline || 'mechanical',
        duration_value:       item.duration_value || '',
        duration_unit:        item.duration_unit || 'months',
        tuition_fees:         item.tuition_fees || item.course_fees || '',
        university_board:     item.university_board || '',
        selected_topics:      item.selected_topics || [],
        num_ndt_certificates: item.num_ndt_certificates || 0,
        num_nactet_certificates: item.num_nactet_certificates || 0,
        num_experience_certificates: item.num_experience_certificates || 0,
        ndt_cutoff_marks:     item.ndt_cutoff_marks || 60,
        nactet_cutoff_marks:  item.nactet_cutoff_marks || 50,
      });
    } else if (type === 'topic') {
      setTopicForm({
        topic_name:    item.topic_name || '',
        short_code:    item.short_code || '',
        is_lab:        item.is_lab || false,
        duration_days: item.duration_days || '',
        fees:          item.fees || '',
        discipline:    item.discipline || 'mechanical',
      });
    } else if (type === 'faculty') {
      setFacultyForm({
        faculty_name:   item.faculty_name || '',
        specialization: item.specialization || '',
        qualification:  item.qualification || '',
        contact_number: item.contact_number || '',
        email:          item.email || '',
        is_active:      item.is_active !== false,
      });
    }
    setShowModal(type);
  }

  // ── OPEN NEW ──
  function openNew(type) {
    setEditing(null);
    if (type === 'batch') setBatchForm({ batch_name: '', discipline: 'mechanical', centre: 'EKM', start_date: '', end_date: '', handling_staff_id: '', max_capacity: 50, status: 'upcoming', remarks: '' });
    if (type === 'college') setCollegeForm({ college_name: '', university: '', district: '', state: '', contact_person: '', contact_number: '', email: '', college_short_code: '', current_status: 'Active' });
    if (type === 'course') setCourseForm({ course_name: '', course_short_code: '', discipline: 'mechanical', duration_value: '', duration_unit: 'months', tuition_fees: '', university_board: '', selected_topics: [], num_ndt_certificates: 0, num_nactet_certificates: 0, ndt_cutoff_marks: 60, nactet_cutoff_marks: 50 });
    if (type === 'topic') setTopicForm({ topic_name: '', short_code: '', duration_days: '', fees: '', discipline: 'mechanical', is_lab: false });
    if (type === 'faculty') setFacultyForm({ faculty_name: '', specialization: '', qualification: '', contact_number: '', email: '', is_active: true });
    setShowModal(type);
  }

  // ── DELETE ──
  async function deleteRecord(table, id, label) {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success(`${label} deleted`); loadAll(); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Master Data</h1><p className="page-subtitle">Manage batches, colleges, course packages and topics</p></div>
        {canEdit('master_data') && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{display:'flex',gap:8}}>
              {activeTab === 'batches'  && <ImportButton configKey="batches"         onImported={()=>loadAll()}/>}
              {activeTab === 'colleges' && <ImportButton configKey="colleges"        onImported={()=>loadAll()}/>}
              {activeTab === 'courses'  && <ImportButton configKey="course_packages" onImported={()=>loadAll()}/>}
              {activeTab === 'topics'   && <ImportButton configKey="topics"          onImported={()=>loadAll()}/>}
              {activeTab === 'acct_cats'    && <ImportButton configKey="accounts_categories"   onImported={()=>loadAcctMaster()}/>}
              {activeTab === 'acct_parties' && <ImportButton configKey="accounts_parties"      onImported={()=>loadAcctMaster()}/>}
              {activeTab === 'acct_paymodes'&& <ImportButton configKey="accounts_payment_modes" onImported={()=>loadAcctMaster()}/>}
              {activeTab === 'batches'  && <button className="btn btn-primary" onClick={() => openNew('batch')}><Plus size={14} /> New Batch</button>}
              {activeTab === 'colleges' && <button className="btn btn-primary" onClick={() => openNew('college')}><Plus size={14} /> New College</button>}
              {activeTab === 'courses'  && <button className="btn btn-primary" onClick={() => openNew('course')}><Plus size={14} /> New Course Package</button>}
              {activeTab === 'topics'   && <button className="btn btn-primary" onClick={() => openNew('topic')}><Plus size={14} /> New Topic</button>}
              {activeTab === 'faculty'  && <button className="btn btn-primary" onClick={() => openNew('faculty')}><Plus size={14} /> New Faculty</button>}
            </div>
          </div>
        )}
      </div>

      <div className="tabs">
        {[['batches','Batches'],['colleges','Colleges'],['courses','Course Packages'],['topics','Topics'],['faculty','Faculty'],['acct_cats','A/C Categories'],['acct_parties','Parties'],['acct_paymodes','Pay Modes'],['qualifications','Qualifications']].map(([v,l]) => (
          <div key={v} className={`tab ${activeTab === v ? 'active' : ''}`} onClick={() => { setActiveTab(v); setSearch(''); clearSel(); setSelectedAcct(new Set()); }}>{l}</div>
        ))}
      </div>

      {/* Search bar */}
      <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
        <div className="search-box" style={{flex:1,maxWidth:320}}>
          <Search size={14}/>
          <input placeholder="Search..." value={search} onChange={e=>{setSearch(e.target.value);clearSel();}}/>
        </div>
        {selected.size>0&&(
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <span style={{fontSize:12,color:'var(--accent)',fontWeight:600}}>{selected.size} selected</span>
            <button className="btn btn-danger btn-sm" onClick={async()=>{
              if(!window.confirm(`Delete ${selected.size} records?`)) return;
              const table = activeTab==='batches'?'batches':activeTab==='colleges'?'colleges':activeTab==='courses'?'course_packages':activeTab==='topics'?'topics':'faculties';
              const { error: bdErr } = await supabase.from(table).delete().in('id',[...selected]);
              if (bdErr) { toast.error('Delete failed (record may be in use): ' + bdErr.message); return; }
              clearSel(); loadAll();
              toast.success('Deleted');
            }}>🗑 Delete Selected</button>
          </div>
        )}
      </div>

      {selectedAcct.size>0 && activeTab.startsWith('acct_') && (
        <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:12}}>
          <span style={{fontSize:12,color:'var(--accent)',fontWeight:600}}>{selectedAcct.size} selected</span>
          <button className="btn btn-danger btn-sm" onClick={async()=>{
            if(!window.confirm(`Delete ${selectedAcct.size} records?`)) return;
            const table = activeTab==='acct_cats'?'accounts_categories':activeTab==='acct_parties'?'accounts_parties':'accounts_payment_modes';
            const { error: adErr } = await supabase.from(table).delete().in('id',[...selectedAcct]);
            if (adErr) { toast.error('Delete failed: ' + adErr.message); return; }
            setSelectedAcct(new Set()); loadAcctMaster(); toast.success('Deleted');
          }}>🗑 Delete Selected</button>
        </div>
      )}

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <>
          {/* ── BATCHES ── */}
          {activeTab === 'batches' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{width:36}}><input type="checkbox" checked={selected.size>0&&batches.every(b=>selected.has(b.id))} onChange={e=>setSelected(e.target.checked?new Set(batches.map(b=>b.id)):new Set())}/></th>
                    <th>Code</th>
                    <th>Batch Name</th>
                    <th>Discipline</th>
                    <th>Centre</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Capacity</th>
                    <th>Handling Staff</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(()=>{const filtered=batches.filter(b=>!search||[b.batch_name,b.batch_code,b.discipline,b.centre,b.status].some(v=>v?.toLowerCase().includes(search.toLowerCase())));return filtered.length===0
                    ? <tr><td colSpan={11}><div className="empty-state"><p>No batches found</p></div></td></tr>
                    : filtered.map(b => (
                      <tr key={b.id}>
                        <td><input type="checkbox" checked={selected.has(b.id)} onChange={()=>toggleSel(b.id)}/></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{b.batch_code}</td>
                        <td style={{ fontWeight: 500 }}>{b.batch_name}</td>
                        <td><span style={{fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:600,background:b.discipline==='mechanical'?'#dbeafe':b.discipline==='civil'?'#dcfce7':'#f1f5f9',color:b.discipline==='mechanical'?'#1d4ed8':b.discipline==='civil'?'#16a34a':'#64748b'}}>{b.discipline||'—'}</span></td>
                        <td><span className="badge badge-amber" style={{fontSize:10}}>{b.centre||'—'}</span></td>
                        <td><span className={`badge ${b.status === 'ongoing' ? 'badge-green' : b.status === 'upcoming' ? 'badge-amber' : 'badge-gray'}`}>{b.status}</span></td>
                        <td style={{ fontSize: 12 }}>{fmtDMY(b.start_date) || '—'}</td>
                        <td style={{ fontSize: 12 }}>{fmtDMY(b.end_date) || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{b.max_capacity}</td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{b.staff?.full_name || '—'}</td>
                        <td>{canEdit('master_data') && <div style={{display:'flex',gap:4}}><button className="btn btn-ghost btn-sm" onClick={() => openEdit('batch', b)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" title="Coordinator History" onClick={async()=>{ const {data}=await supabase.from('batch_coordinator_history').select('*').eq('batch_id',b.id).order('from_date',{ascending:false}); setCoordHistory(data||[]); setShowCoordHistory(b.id); }}>📋</button><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => deleteRecord('batches', b.id, b.batch_name)}><Trash2 size={13} /></button></div>}</td>
                      </tr>
                    ))})()}
                </tbody>
              </table>
            </div>
          )}

          {/* ── COLLEGES ── */}
          {activeTab === 'colleges' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{width:36}}><input type="checkbox" checked={selected.size>0&&colleges.every(x=>selected.has(x.id))} onChange={e=>setSelected(e.target.checked?new Set(colleges.map(x=>x.id)):new Set())}/></th>
                    <th>Short Code</th>
                    <th>College Name</th>
                    <th>Status</th>
                    <th>Zone</th>
                    <th>University</th>
                    <th>District</th>
                    <th>State</th>
                    <th>Contact Person</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(()=>{const filtered=colleges.filter(col=>!search||[col.college_name,col.college_short_code,col.district,col.state,col.university,col.zone].some(v=>v?.toLowerCase().includes(search.toLowerCase())));return filtered.length===0
                    ? <tr><td colSpan={10}><div className="empty-state"><p>No colleges found</p></div></td></tr>
                    : filtered.map(col => (
                      <tr key={col.id}>
                        <td><input type="checkbox" checked={selected.has(col.id)} onChange={()=>toggleSel(col.id)}/></td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)', fontSize:12 }}>{col.college_short_code || '—'}</td>
                        <td style={{ fontWeight: 500 }}>{col.college_name}</td>
                        <td>
                          <span className={`badge ${col.current_status==='Active'?'badge-green':col.current_status==='Shutdown'?'badge-red':'badge-amber'}`} style={{fontSize:9}}>
                            {col.current_status||'Active'}
                          </span>
                        </td>
                        <td style={{fontSize:12}}>{col.zone||'—'}</td>
                        <td style={{ fontSize: 12 }}>{col.university || '—'}</td>
                        <td style={{ fontSize: 12 }}>{col.district || '—'}</td>
                        <td style={{ fontSize: 12 }}>{col.state || '—'}</td>
                        <td style={{ fontSize: 12 }}>{col.contact_person || '—'}</td>
                        <td>{canEdit('master_data') && <div style={{display:'flex',gap:4}}><button className="btn btn-ghost btn-sm" onClick={() => openEdit('college', col)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => deleteRecord('colleges', col.id, col.college_name)}><Trash2 size={13} /></button></div>}</td>
                      </tr>
                    ))})()}
                </tbody>
              </table>
            </div>
          )}

          {/* ── COURSE PACKAGES ── */}
          {activeTab === 'courses' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th style={{width:36}}><input type="checkbox" checked={selected.size>0&&courses.every(x=>selected.has(x.id))} onChange={e=>setSelected(e.target.checked?new Set(courses.map(x=>x.id)):new Set())}/></th><th>Code</th><th>Course Name</th><th>Short Code</th><th>University/Board</th><th>Discipline</th><th>Duration</th><th>Tuition Fees</th><th>Topics</th><th>NDT Certs</th><th>NACTET Certs</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {(()=>{const filtered=courses.filter(x=>!search||[x.course_name,x.discipline,String(x.course_fees||'')].some(v=>v?.toLowerCase().includes(search.toLowerCase())));return filtered.length===0
                    ? <tr><td colSpan={11}><div className="empty-state"><p>No courses yet</p></div></td></tr>
                    : filtered.map(cp2 => (
                      <tr key={cp2.id}>
                        <td><input type="checkbox" checked={selected.has(cp2.id)} onChange={()=>toggleSel(cp2.id)}/></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{cp2.course_code}</td>
                        <td style={{ fontWeight: 500 }}>{cp2.course_name}</td>
                        <td><span style={{ fontFamily:'monospace', fontSize:11, background:'var(--accent-light)', color:'var(--accent)', padding:'2px 8px', borderRadius:4, fontWeight:700 }}>{cp2.course_short_code||'—'}</span></td>
                        <td>
                          {cp2.university_board
                            ? <span className="badge badge-purple">{cp2.university_board}</span>
                            : <span style={{ color: 'var(--text3)' }}>—</span>
                          }
                        </td>
                        <td><span style={{fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:600,background:cp2.discipline==='mechanical'?'#dbeafe':cp2.discipline==='civil'?'#dcfce7':'#f1f5f9',color:cp2.discipline==='mechanical'?'#1d4ed8':cp2.discipline==='civil'?'#16a34a':'#64748b'}}>{cp2.discipline||'—'}</span></td>
                        <td style={{ fontSize: 12 }}>{cp2.duration_value} {cp2.duration_unit}</td>
                        <td style={{ fontWeight: 600 }}>₹{Number(cp2.tuition_fees || cp2.course_fees || 0).toLocaleString('en-IN')}</td>
                        <td>
                          {cp2.selected_topics && cp2.selected_topics.length > 0
                            ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {cp2.selected_topics.slice(0, 4).map(id => {
                                  const t = topics.find(tp => tp.id === id);
                                  return t ? <span key={id} className="badge badge-cyan" style={{ fontSize: 10 }}>{t.short_code || t.topic_name.slice(0, 4)}</span> : null;
                                })}
                                {cp2.selected_topics.length > 4 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>+{cp2.selected_topics.length - 4}</span>}
                              </div>
                            : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ textAlign: 'center' }}>{cp2.num_ndt_certificates || 0}</td>
                        <td style={{ textAlign: 'center' }}>{cp2.num_nactet_certificates || 0}</td>
                        <td>{canEdit('master_data') && <div style={{display:'flex',gap:4}}><button className="btn btn-ghost btn-sm" onClick={() => openEdit('course', cp2)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => deleteRecord('course_packages', cp2.id, cp2.course_name)}><Trash2 size={13} /></button></div>}</td>
                      </tr>
                    ))})()}
                </tbody>
              </table>
            </div>
          )}

          {/* ── TOPICS ── */}
          {activeTab === 'topics' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th style={{width:36}}><input type="checkbox" checked={selected.size>0&&topics.every(x=>selected.has(x.id))} onChange={e=>setSelected(e.target.checked?new Set(topics.map(x=>x.id)):new Set())}/></th><th>#</th><th>Topic Name</th><th>Short Code</th><th>Discipline</th><th>Duration (Days)</th><th style={{textAlign:'center'}}>Lab</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {(()=>{const filtered=topics.filter(x=>!search||[x.topic_name,x.short_code,x.discipline].some(v=>v?.toLowerCase().includes(search.toLowerCase())));return filtered.length===0
                    ? <tr><td colSpan={7}><div className="empty-state"><p>No topics yet. Add topics like UT, RT, MT...</p></div></td></tr>
                    : filtered.map((t, i) => (
                      <tr key={t.id}>
                        <td><input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggleSel(t.id)}/></td>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{t.topic_name}</td>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--accent-light)', color: 'var(--accent)', padding: '2px 8px', borderRadius: 4 }}>
                            {t.short_code || '—'}
                          </span>
                        </td>
                        <td>
                          <span style={{fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:600,background:t.discipline==='mechanical'?'#dbeafe':t.discipline==='civil'?'#dcfce7':'#f1f5f9',color:t.discipline==='mechanical'?'#1d4ed8':t.discipline==='civil'?'#16a34a':'#64748b'}}>{t.discipline||'—'}</span>
                        </td>
                        <td>
                          {t.duration_days > 0
                            ? <span className="badge badge-blue">{t.duration_days} day{t.duration_days > 1 ? 's' : ''}</span>
                            : <span style={{ color: 'var(--text3)' }}>—</span>
                          }
                        </td>
                        <td style={{textAlign:'center'}}>
                          <button onClick={async()=>{const nv=!t.is_lab;const {error:te}=await supabase.from('topics').update({is_lab:nv}).eq('id',t.id);if(te){toast.error(te.message);return;}loadAll();toast.success(nv?'Marked as Lab':'Lab tag removed');}} style={{background:'none',border:'none',cursor:'pointer',fontSize:16}} title={t.is_lab?'Lab topic':'Not a lab'}>
                            {t.is_lab?'🧪':'○'}
                          </button>
                        </td>
                        <td>{canEdit('master_data') && <div style={{display:'flex',gap:4}}><button className="btn btn-ghost btn-sm" onClick={() => openEdit('topic', t)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => deleteRecord('topics', t.id, t.topic_name)}><Trash2 size={13} /></button></div>}</td>
                      </tr>
                    ))})()}
                </tbody>
              </table>
            </div>
          )}

          {/* ── FACULTY TAB ── */}
          {activeTab === 'faculty' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th style={{width:36}}><input type="checkbox" checked={selected.size>0&&faculties.every(x=>selected.has(x.id))} onChange={e=>setSelected(e.target.checked?new Set(faculties.map(x=>x.id)):new Set())}/></th><th>#</th><th>Faculty Name</th><th>Specialization</th><th>Qualification</th><th>Contact</th><th>Email</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {(()=>{const filtered=faculties.filter(x=>!search||[x.faculty_name,x.specialization,x.qualification,x.contact_number,x.email].some(v=>v?.toLowerCase().includes(search.toLowerCase())));return filtered.length===0
                    ? <tr><td colSpan={9}><div className="empty-state"><p>No faculty found</p></div></td></tr>
                    : filtered.map((f, i) => (
                      <tr key={f.id}>
                        <td><input type="checkbox" checked={selected.has(f.id)} onChange={()=>toggleSel(f.id)}/></td>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{f.faculty_name}</td>
                        <td style={{ fontSize: 12 }}>{f.specialization || '—'}</td>
                        <td style={{ fontSize: 12 }}>{f.qualification || '—'}</td>
                        <td style={{ fontSize: 12 }}>{f.contact_number || '—'}</td>
                        <td style={{ fontSize: 12 }}>{f.email || '—'}</td>
                        <td><span className={`badge ${f.is_active ? 'badge-green' : 'badge-red'}`}>{f.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>{canEdit('master_data') && <div style={{display:'flex',gap:4}}><button className="btn btn-ghost btn-sm" onClick={() => openEdit('faculty', f)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => deleteRecord('faculties', f.id, f.faculty_name)}><Trash2 size={13} /></button></div>}</td>
                      </tr>
                    ))})()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── FACULTY MODAL ── */}
          {/* ── ACCOUNTS CATEGORIES TAB ── */}
          {activeTab === 'qualifications' && <QualificationsTab canEdit={canEdit('master_data')} />}

          {activeTab === 'acct_cats' && (
            <div>
              <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                {editingCat && <div style={{fontSize:11,color:'var(--accent)',fontWeight:600,padding:'4px 8px',background:'var(--accent-light)',borderRadius:6}}>Editing: {editingCat.name} <button onClick={()=>{setEditingCat(null);setAcctCatForm({name:'',type:'in',category_type:'General'});}} style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',marginLeft:4}}>✕</button></div>}
                <input value={acctCatForm.name} onChange={e=>setAcctCatForm(f=>({...f,name:e.target.value}))} placeholder="Category name e.g. Tuition Fees" style={{flex:2,minWidth:200}}/>
                <select value={acctCatForm.type} onChange={e=>setAcctCatForm(f=>({...f,type:e.target.value}))} style={{minWidth:160}}>
                  <option value="in">Income (Cash In)</option>
                  <option value="out">Expense (Cash Out)</option>
                  <option value="both">Both</option>
                </select>
                <select value={acctCatForm.category_type||'General'} onChange={e=>setAcctCatForm(f=>({...f,category_type:e.target.value}))} style={{minWidth:140}}>
                  <option value="Training">Training</option>
                  <option value="Construction">Construction</option>
                  <option value="Finance">Finance</option>
                  <option value="General">General</option>
                </select>
                <button className="btn btn-primary" onClick={async()=>{
                  if(!acctCatForm.name.trim())return;
                  const nm = acctCatForm.name.trim();
                  const r = editingCat
                    ? await supabase.from('accounts_categories').update({name:nm,type:acctCatForm.type,category_type:acctCatForm.category_type}).eq('id',editingCat.id)
                    : await supabase.from('accounts_categories').insert({...acctCatForm,name:nm,company:'ALL'});
                  if (r.error) { toast.error(r.error.message); return; }
                  setEditingCat(null);
                  setAcctCatForm({name:'',type:'in',category_type:'General'});
                  loadAcctMaster();
                }}><Plus size={14}/> Add</button>
              </div>
              <table><thead><tr><th style={{width:36}}><input type="checkbox" checked={selectedAcct.size===acctCats.length&&acctCats.length>0} onChange={e=>setSelectedAcct(e.target.checked?new Set(acctCats.map(c=>c.id)):new Set())}/></th><th>Category</th><th>Type</th><th>Action</th></tr></thead>
                <tbody>{acctCats.filter(c=>!search||(c.name||'').toLowerCase().includes(search.toLowerCase())).map(cat=>(
                  <tr key={cat.id}>
                    <td><input type="checkbox" checked={selectedAcct.has(cat.id)} onChange={()=>setSelectedAcct(p=>{const n=new Set(p);n.has(cat.id)?n.delete(cat.id):n.add(cat.id);return n;})}/></td>
                    <td style={{fontWeight:500}}>{cat.name}</td>
                    <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:8,fontWeight:600,
                      background:{'Training':'#dbeafe','Construction':'#fef3c7','Finance':'#ede9fe','General':'#f3f4f6'}[cat.category_type||'General'],
                      color:{'Training':'#1d4ed8','Construction':'#d97706','Finance':'#7c3aed','General':'#6b7280'}[cat.category_type||'General']}}>
                      {cat.category_type||'General'}</span></td>
                    <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:12,fontWeight:600,background:cat.type==='in'?'#dcfce7':cat.type==='out'?'#fee2e2':'#f1f5f9',color:cat.type==='in'?'#16a34a':cat.type==='out'?'#dc2626':'#64748b'}}>{cat.type==='in'?'Income':cat.type==='out'?'Expense':'Both'}</span></td>
                    <td><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm(`Delete "${cat.name}"?`))return;const {error:de}=await supabase.from('accounts_categories').delete().eq('id',cat.id);if(de){toast.error(de.message);return;}loadAcctMaster();}}><Trash2 size={13}/></button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingCat(cat);setAcctCatForm({name:cat.name,type:cat.type,category_type:cat.category_type||'General'});}}><Edit2 size={13}/></button>
                      </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* ── ACCOUNTS PARTIES TAB ── */}
          {activeTab === 'acct_parties' && (
            <div>
              <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                {editingParty && <div style={{fontSize:11,color:'var(--accent)',fontWeight:600,padding:'4px 8px',background:'var(--accent-light)',borderRadius:6}}>Editing: {editingParty.party_name} <button onClick={()=>{setEditingParty(null);setAcctPartyForm({party_name:'',party_type:'customer',contact:''});}} style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',marginLeft:4}}>✕</button></div>}
                <input value={acctPartyForm.party_name} onChange={e=>setAcctPartyForm(f=>({...f,party_name:e.target.value}))} placeholder="Party / Company name" style={{flex:2,minWidth:200}}/>
                <select value={acctPartyForm.party_type} onChange={e=>setAcctPartyForm(f=>({...f,party_type:e.target.value}))}>
                  <option value="customer">Customer</option>
                  <option value="supplier">Supplier</option>
                  <option value="both">Both</option>
                </select>
                <input value={acctPartyForm.contact} onChange={e=>setAcctPartyForm(f=>({...f,contact:e.target.value}))} placeholder="Contact (optional)" style={{flex:1,minWidth:150}}/>
                <button className="btn btn-primary" onClick={async()=>{
                  if(!acctPartyForm.party_name.trim())return;
                  const pf = {...acctPartyForm, party_name: acctPartyForm.party_name.trim()};
                  const r = editingParty
                    ? await supabase.from('accounts_parties').update(pf).eq('id',editingParty.id)
                    : await supabase.from('accounts_parties').insert(pf);
                  if (r.error) { toast.error(r.error.message); return; }
                  setEditingParty(null);
                  setAcctPartyForm({party_name:'',party_type:'customer',contact:''});
                  loadAcctMaster();
                }}><Plus size={14}/> Add</button>
              </div>
              <table><thead><tr><th style={{width:36}}><input type="checkbox" checked={selectedAcct.size===acctParties.length&&acctParties.length>0} onChange={e=>setSelectedAcct(e.target.checked?new Set(acctParties.map(p=>p.id)):new Set())}/></th><th>Party Name</th><th>Type</th><th>Contact</th><th>Action</th></tr></thead>
                <tbody>{acctParties.filter(p=>!search||(p.party_name||'').toLowerCase().includes(search.toLowerCase())).map(p=>(
                  <tr key={p.id}>
                    <td><input type="checkbox" checked={selectedAcct.has(p.id)} onChange={()=>setSelectedAcct(prev=>{const n=new Set(prev);n.has(p.id)?n.delete(p.id):n.add(p.id);return n;})}/></td>
                    <td style={{fontWeight:500}}>{p.party_name}</td>
                    <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:12,fontWeight:600,background:p.party_type==='customer'?'#dbeafe':p.party_type==='supplier'?'#fef3c7':'#f1f5f9',color:p.party_type==='customer'?'#1d4ed8':p.party_type==='supplier'?'#92400e':'#64748b'}}>{p.party_type}</span></td>
                    <td style={{fontSize:12,color:'var(--text3)'}}>{p.contact||'—'}</td>
                    <td style={{display:'flex',gap:4}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingParty(p);setAcctPartyForm({party_name:p.party_name,party_type:p.party_type,contact:p.contact||''});}}><Edit2 size={13}/></button>
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm(`Delete "${p.party_name}"?`))return;const {error:de}=await supabase.from('accounts_parties').delete().eq('id',p.id);if(de){toast.error(de.message);return;}loadAcctMaster();}}><Trash2 size={13}/></button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* ── PAYMENT MODES TAB ── */}
          {activeTab === 'acct_paymodes' && (
            <div>
              <div style={{display:'flex',gap:8,marginBottom:12}}>
                {editingPayMode && <div style={{fontSize:11,color:'var(--accent)',fontWeight:600,padding:'4px 8px',background:'var(--accent-light)',borderRadius:6}}>Editing: {editingPayMode.name} <button onClick={()=>{setEditingPayMode(null);setAcctPayForm({name:''});}} style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',marginLeft:4}}>✕</button></div>}
                <input value={acctPayForm.name} onChange={e=>setAcctPayForm(f=>({...f,name:e.target.value}))} placeholder="Payment mode name e.g. NEFT" style={{flex:1}}/>
                <button className="btn btn-primary" onClick={async()=>{
                  if(!acctPayForm.name.trim())return;
                  const pm = {...acctPayForm, name: acctPayForm.name.trim()};
                  const r = editingPayMode
                    ? await supabase.from('accounts_payment_modes').update(pm).eq('id',editingPayMode.id)
                    : await supabase.from('accounts_payment_modes').insert(pm);
                  if (r.error) { toast.error(r.error.message); return; }
                  setEditingPayMode(null);
                  setAcctPayForm({name:''});
                  loadAcctMaster();
                }}><Plus size={14}/> Add</button>
              </div>
              <table><thead><tr><th style={{width:36}}><input type="checkbox" checked={selectedAcct.size===acctPayModes.length&&acctPayModes.length>0} onChange={e=>setSelectedAcct(e.target.checked?new Set(acctPayModes.map(m=>m.id)):new Set())}/></th><th>Payment Mode</th><th>Action</th></tr></thead>
                <tbody>{acctPayModes.filter(m=>!search||(m.name||'').toLowerCase().includes(search.toLowerCase())).map(m=>(
                  <tr key={m.id}>
                    <td><input type="checkbox" checked={selectedAcct.has(m.id)} onChange={()=>setSelectedAcct(prev=>{const n=new Set(prev);n.has(m.id)?n.delete(m.id):n.add(m.id);return n;})}/></td>
                    <td style={{fontWeight:500}}>{m.name}</td>
                    <td style={{display:'flex',gap:4}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingPayMode(m);setAcctPayForm({name:m.name});}}><Edit2 size={13}/></button>
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm(`Delete "${m.name}"?`))return;const {error:de}=await supabase.from('accounts_payment_modes').delete().eq('id',m.id);if(de){toast.error(de.message);return;}loadAcctMaster();}}><Trash2 size={13}/></button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

      {showModal === 'faculty' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Faculty' : 'New Faculty'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Faculty Name *</label>
                  <input value={facultyForm.faculty_name} onChange={e => setFacultyForm(f => ({ ...f, faculty_name: e.target.value }))} placeholder="e.g. Dr. John Smith" />
                </div>
                <div className="form-group">
                  <label>Specialization</label>
                  <input value={facultyForm.specialization} onChange={e => setFacultyForm(f => ({ ...f, specialization: e.target.value }))} placeholder="e.g. NDT, Welding, QA/QC" />
                </div>
                <div className="form-group">
                  <label>Qualification</label>
                  <input value={facultyForm.qualification} onChange={e => setFacultyForm(f => ({ ...f, qualification: e.target.value }))} placeholder="e.g. B.E, M.Tech, ASNT Level III" />
                </div>
                <div className="form-group">
                  <label>Contact Number</label>
                  <input value={facultyForm.contact_number} onChange={e => setFacultyForm(f => ({ ...f, contact_number: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={facultyForm.email} onChange={e => setFacultyForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={facultyForm.is_active ? 'active' : 'inactive'} onChange={e => setFacultyForm(f => ({ ...f, is_active: e.target.value === 'active' }))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveFaculty} disabled={saving}>{saving ? 'Saving...' : 'Save Faculty'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── BATCH MODAL ── */}
      {showModal === 'batch' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Batch' : 'New Batch'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Batch Name *</label>
                  <input value={batchForm.batch_name} onChange={e => setBatchForm(f => ({ ...f, batch_name: e.target.value }))} placeholder="e.g. Batch Jan 2025" />
                </div>
                <div className="form-group">
                  <label>Discipline</label>
                  <select value={batchForm.discipline} onChange={e => setBatchForm(f => ({ ...f, discipline: e.target.value }))}>
                    <option value="mechanical">Mechanical</option>
                    <option value="civil">Civil</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Centre</label>
                  <select value={batchForm.centre||'EKM'} onChange={e => setBatchForm(f => ({ ...f, centre: e.target.value }))}>
                    <option value="EKM">EKM</option>
                    <option value="TVM">TVM</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Handling Staff</label>
                  <select value={batchForm.handling_staff_id} onChange={e => setBatchForm(f => ({ ...f, handling_staff_id: e.target.value }))}>
                    <option value="">Select Staff</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={batchForm.status} onChange={e => setBatchForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="upcoming">Upcoming</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <DateInput value={batchForm.start_date} onChange={e => setBatchForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <DateInput value={batchForm.end_date} onChange={e => setBatchForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Max Capacity</label>
                  <input type="number" value={batchForm.max_capacity} onChange={e => setBatchForm(f => ({ ...f, max_capacity: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label>Remarks</label>
                  <textarea value={batchForm.remarks||''} onChange={e => setBatchForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Any notes about this batch..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBatch} disabled={saving}>{saving ? 'Saving...' : 'Save Batch'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COLLEGE MODAL ── */}
      {showModal === 'college' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit College' : 'New College'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full"><label>College Name *</label><input value={collegeForm.college_name} onChange={e => setCollegeForm(f => ({ ...f, college_name: e.target.value }))} /></div>
              <div className="form-group"><label>Current Status</label>
                <select value={collegeForm.current_status||'Active'} onChange={e => setCollegeForm(f => ({ ...f, current_status: e.target.value }))}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Shutdown">Shutdown</option>
                </select>
              </div>
              <div className="form-group"><label>Zone</label>
                <select value={collegeForm.zone||''} onChange={e => setCollegeForm(f => ({ ...f, zone: e.target.value }))}>
                  <option value="">— Select Zone —</option>
                  {['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5'].map(z=><option key={z} value={z}>{z}</option>)}
                </select>
              </div>
                <div className="form-group"><label>College Short Code</label><input value={collegeForm.college_short_code} onChange={e => setCollegeForm(f => ({ ...f, college_short_code: e.target.value.toUpperCase() }))} placeholder="e.g. MEC, TKM, CET" style={{ fontFamily: 'monospace', fontWeight: 600 }} /></div>
                <div className="form-group"><label>University</label><select value={collegeForm.university||''} onChange={e => setCollegeForm(f => ({ ...f, university: e.target.value }))}><option value="">Select University</option><option>APJ</option><option>POLY</option><option>ANNA</option><option>CALICUT</option><option>MG</option><option>KANNUR</option><option>KERALA</option><option>CUSAT</option><option>NACTET</option><option>Other</option></select></div>
                <div className="form-group"><label>District</label><select value={collegeForm.district||''} onChange={e => setCollegeForm(f => ({ ...f, district: e.target.value }))}><option value="">Select District</option><option>Thiruvananthapuram</option><option>Kollam</option><option>Pathanamthitta</option><option>Alappuzha</option><option>Kottayam</option><option>Idukki</option><option>Ernakulam</option><option>Thrissur</option><option>Palakkad</option><option>Malappuram</option><option>Kozhikode</option><option>Wayanad</option><option>Kannur</option><option>Kasaragod</option><option>Other</option></select></div>
                <div className="form-group"><label>State</label><select value={collegeForm.state||''} onChange={e => setCollegeForm(f => ({ ...f, state: e.target.value }))}><option value="">Select State</option><option>Kerala</option><option>Tamil Nadu</option><option>Karnataka</option><option>Andhra Pradesh</option><option>Telangana</option><option>Maharashtra</option><option>Gujarat</option><option>Rajasthan</option><option>Delhi</option><option>Other</option></select></div>
                <div className="form-group"><label>Contact Person</label><input value={collegeForm.contact_person} onChange={e => setCollegeForm(f => ({ ...f, contact_person: e.target.value }))} /></div>
                <div className="form-group"><label>Contact Number</label><input value={collegeForm.contact_number} onChange={e => setCollegeForm(f => ({ ...f, contact_number: e.target.value }))} /></div>
                <div className="form-group"><label>Email</label><input type="email" value={collegeForm.email} onChange={e => setCollegeForm(f => ({ ...f, email: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCollege} disabled={saving}>{saving ? 'Saving...' : 'Save College'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COURSE PACKAGE MODAL ── */}
      {showModal === 'course' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Course Package' : 'New Course Package'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-heading">Basic Details</div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>Course Name *</label>
                  <input value={courseForm.course_name} onChange={e => setCourseForm(f => ({ ...f, course_name: e.target.value }))} placeholder="e.g. NDT Level 2 Package" />
                </div>
                <div className="form-group">
                  <label>Course Short Code</label>
                  <input value={courseForm.course_short_code||''} onChange={e => setCourseForm(f => ({ ...f, course_short_code: e.target.value.toUpperCase() }))} placeholder="e.g. PGDCEM, DCEM" maxLength={20} />
                </div>
                <div className="form-group">
                  <label>University / Board *</label>
                  <select value={courseForm.university_board} onChange={e => setCourseForm(f => ({ ...f, university_board: e.target.value }))}>
                    <option value="">Select University / Board</option>
                    <option value="NACTET">NACTET</option>
                    <option value="NDT">NDT (ASNT)</option>
                    <option value="API">API (American Petroleum Institute)</option>
                    <option value="AWS">AWS (American Welding Society)</option>
                    <option value="NEBOSH">NEBOSH</option>
                    <option value="IOSH">IOSH</option>
                    <option value="CSWIP">CSWIP</option>
                    <option value="PCN">PCN</option>
                    <option value="ISO">ISO</option>
                    <option value="ASME">ASME</option>
                    <option value="TWI">TWI</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Discipline</label>
                  <select value={courseForm.discipline} onChange={e => setCourseForm(f => ({ ...f, discipline: e.target.value }))}>
                    <option value="mechanical">Mechanical</option>
                    <option value="civil">Civil</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Duration</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="number" value={courseForm.duration_value} onChange={e => setCourseForm(f => ({ ...f, duration_value: e.target.value }))} style={{ width: 80 }} placeholder="0" />
                    <select value={courseForm.duration_unit} onChange={e => setCourseForm(f => ({ ...f, duration_unit: e.target.value }))} style={{ flex: 1 }}>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Tuition Fees (₹)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>₹</span>
                    <input type="number" value={courseForm.tuition_fees} onChange={e => setCourseForm(f => ({ ...f, tuition_fees: e.target.value }))} placeholder="0" style={{ paddingLeft: 24 }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>No. of NDT Certificates</label>
                  <input type="number" min="0" value={courseForm.num_ndt_certificates} onChange={e => setCourseForm(f => ({ ...f, num_ndt_certificates: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>No. of NACTET Certificates</label>
                  <input type="number" min="0" value={courseForm.num_nactet_certificates} onChange={e => setCourseForm(f => ({ ...f, num_nactet_certificates: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>No. of Experience Certificates</label>
                  <input type="number" min="0" value={courseForm.num_experience_certificates} onChange={e => setCourseForm(f => ({ ...f, num_experience_certificates: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>NDT Cut-off Marks</label>
                  <input type="number" value={courseForm.ndt_cutoff_marks} onChange={e => setCourseForm(f => ({ ...f, ndt_cutoff_marks: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>NACTET Cut-off Marks</label>
                  <input type="number" value={courseForm.nactet_cutoff_marks} onChange={e => setCourseForm(f => ({ ...f, nactet_cutoff_marks: e.target.value }))} />
                </div>
              </div>

              {/* Topics multi-select */}
              <div className="form-section-heading" style={{ marginTop: 20 }}>Select Topics for this Course</div>
              {topics.length === 0 ? (
                <div style={{ padding: '12px 14px', background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, fontSize: 12, color: 'var(--amber)' }}>
                  ⚠️ No topics found. Go to the Topics tab to add topics first.
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>

                  {/* ── Search & Filter bar ── */}
                  <div style={{ display: 'flex', gap: 8, padding: '8px 10px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 140, background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px' }}>
                      <Search size={12} style={{ color: 'var(--text3)', flexShrink: 0 }}/>
                      <input
                        placeholder="Search by name or code..."
                        value={topicSearch}
                        onChange={e => setTopicSearch(e.target.value)}
                        style={{ border: 'none', outline: 'none', fontSize: 12, width: '100%', background: 'transparent' }}
                      />
                      {topicSearch && <span style={{ cursor: 'pointer', color: 'var(--text3)', fontSize: 14 }} onClick={() => setTopicSearch('')}>×</span>}
                    </div>
                    <select value={topicDisciplineFilter} onChange={e => setTopicDisciplineFilter(e.target.value)}
                      style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', height: 30 }}>
                      <option value="">All</option>
                      <option value="mechanical">Mechanical</option>
                      <option value="civil">Civil</option>
                    </select>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => {
                      const ids = topics.filter(t => {
                        const ms = !topicSearch || (t.topic_name||'').toLowerCase().includes(topicSearch.toLowerCase()) || (t.short_code||'').toLowerCase().includes(topicSearch.toLowerCase());
                        const md = !topicDisciplineFilter || (t.discipline||'').toLowerCase() === topicDisciplineFilter.toLowerCase();
                        return ms && md;
                      }).map(t => t.id);
                      setCourseForm(f => ({ ...f, selected_topics: [...new Set([...f.selected_topics, ...ids])] }));
                    }}>+ All</button>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setCourseForm(f => ({ ...f, selected_topics: [] }))}>Clear</button>
                    <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                      {courseForm.selected_topics.length}/{topics.length}
                    </span>
                  </div>

                  {/* ── Topics list ── */}
                  <div style={{ maxHeight: 280, overflowY: 'auto', background: '#fff' }}>
                    {topics.filter(t => {
                      const ms = !topicSearch || (t.topic_name||'').toLowerCase().includes(topicSearch.toLowerCase()) || (t.short_code||'').toLowerCase().includes(topicSearch.toLowerCase());
                      const md = !topicDisciplineFilter || (t.discipline||'').toLowerCase() === topicDisciplineFilter.toLowerCase();
                      return ms && md;
                    }).length === 0 ? (
                      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No topics match your search</div>
                    ) : topics.filter(t => {
                      const ms = !topicSearch || (t.topic_name||'').toLowerCase().includes(topicSearch.toLowerCase()) || (t.short_code||'').toLowerCase().includes(topicSearch.toLowerCase());
                      const md = !topicDisciplineFilter || (t.discipline||'').toLowerCase() === topicDisciplineFilter.toLowerCase();
                      return ms && md;
                    }).map((t, idx) => {
                      const isSelected = courseForm.selected_topics.includes(t.id);
                      return (
                        <label key={t.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', cursor: 'pointer',
                          background: isSelected ? '#eef6ff' : idx % 2 === 0 ? '#fafafa' : '#fff',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background 0.1s',
                        }}>
                          <input type="checkbox" checked={isSelected}
                            onChange={() => setCourseForm(f => ({
                              ...f,
                              selected_topics: isSelected
                                ? f.selected_topics.filter(id => id !== t.id)
                                : [...f.selected_topics, t.id]
                            }))}
                            style={{ width: 15, height: 15, accentColor: 'var(--accent)', flexShrink: 0 }}
                          />
                          <span style={{
                            fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
                            background: isSelected ? 'var(--accent)' : '#e8f0fe',
                            color: isSelected ? '#fff' : 'var(--accent)',
                            padding: '2px 7px', borderRadius: 4, minWidth: 32,
                            textAlign: 'center', flexShrink: 0
                          }}>
                            {t.short_code || '—'}
                          </span>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? 'var(--accent)' : 'var(--text)' }}>
                            {t.topic_name}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
                            {t.discipline ? t.discipline.slice(0,4) + '. ' : ''}{t.duration_days > 0 ? t.duration_days+'d' : ''}
                          </span>
                          {t.fees > 0 && (
                            <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, flexShrink: 0 }}>₹{t.fees.toLocaleString('en-IN')}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {/* ── Selected chips ── */}
                  {courseForm.selected_topics.length > 0 && (
                    <div style={{ padding: '8px 10px', background: '#f0f7ff', borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {courseForm.selected_topics.map(id => {
                        const t = topics.find(tp => tp.id === id);
                        return t ? (
                          <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--accent)', color: '#fff', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                            {t.short_code || t.topic_name}
                            <span style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
                              onClick={() => setCourseForm(f => ({ ...f, selected_topics: f.selected_topics.filter(i => i !== id) }))}>×</span>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Fees Summary */}
              {courseForm.selected_topics.length > 0 && (() => {
                const topicTotal = courseForm.selected_topics.reduce((sum, id) => {
                  const t = topics.find(tp => tp.id === id);
                  return sum + Number(t?.fees || 0);
                }, 0);
                const discount = Number(courseForm.discount || 0);
                return (
                  <div style={{ marginTop: 4, background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 10 }}>Fee Calculation</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Total Topic Fees (₹)</label>
                        <input value={topicTotal.toLocaleString('en-IN')} readOnly style={{ background: 'var(--surface)', fontWeight: 700, color: 'var(--accent)' }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Discount (₹)</label>
                        <input type="number" min="0" value={courseForm.discount || ''} onChange={e => {
                          const disc = Number(e.target.value || 0);
                          setCourseForm(f => ({ ...f, discount: e.target.value, tuition_fees: String(topicTotal - disc) }));
                        }} placeholder="0" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Course Fees (₹)</label>
                        <input value={(topicTotal - discount).toLocaleString('en-IN')} readOnly style={{ background: 'var(--accent-light)', fontWeight: 800, color: 'var(--accent)', fontSize: 15 }} />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {editing && (
                <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, fontSize: 12, color: 'var(--amber)' }}>
                  ⚠️ Saving will automatically update the Tuition Fees in all student profiles enrolled in this course package.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCourse} disabled={saving}>{saving ? 'Saving...' : 'Save Course Package'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TOPIC MODAL ── */}
      {showModal === 'topic' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Topic' : 'New Topic'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Topic Name *</label>
                  <input value={topicForm.topic_name} onChange={e => setTopicForm(f => ({ ...f, topic_name: e.target.value }))} placeholder="e.g. Ultrasonic Testing" />
                </div>
                <div className="form-group">
                  <label>Short Code</label>
                  <input value={topicForm.short_code} onChange={e => setTopicForm(f => ({ ...f, short_code: e.target.value.toUpperCase() }))} placeholder="e.g. UT" maxLength={10} />
                </div>
                <div className="form-group">
                  <label>Discipline</label>
                  <select value={topicForm.discipline||'mechanical'} onChange={e => setTopicForm(f => ({ ...f, discipline: e.target.value }))}>
                    <option value="mechanical">Mechanical</option>
                    <option value="civil">Civil</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Duration (in Days)</label>
                  <input type="number" min="0" value={topicForm.duration_days} onChange={e => setTopicForm(f => ({ ...f, duration_days: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Fees (₹)</label>
                  <input type="number" min="0" value={topicForm.fees || ''} onChange={e => setTopicForm(f => ({ ...f, fees: e.target.value }))} placeholder="0" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTopic} disabled={saving}>{saving ? 'Saving...' : 'Save Topic'}</button>
            </div>
          </div>
        </div>
      )}

      {showCoordHistory && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCoordHistory(null)}>
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-header">
              <span className="modal-title">📋 Coordinator History — {batches.find(b=>b.id===showCoordHistory)?.batch_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowCoordHistory(null)}>✕</button>
            </div>
            <div className="modal-body">
              {coordHistory.length===0?(
                <div className="empty-state"><p>No coordinator change history recorded yet.</p></div>
              ):(
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{background:'var(--accent-light)'}}>
                    <th style={{padding:'8px 12px',textAlign:'left'}}>#</th>
                    <th style={{padding:'8px 12px',textAlign:'left'}}>Coordinator</th>
                    <th style={{padding:'8px 12px',textAlign:'left'}}>From Date</th>
                  </tr></thead>
                  <tbody>
                    {coordHistory.map((h,i)=>(
                      <tr key={h.id} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'8px 12px',color:'var(--text3)'}}>{i+1}</td>
                        <td style={{padding:'8px 12px',fontWeight:500}}>{h.staff_name}</td>
                        <td style={{padding:'8px 12px'}}>{fmtDMY(h.from_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Master Data > Qualifications: the list used by the student profile and the Students filter ──
const qKey = v => String(v || '').trim().toLowerCase();
function QualificationsTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [q, setQ] = useState('');
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('qualifications').select('*').order('sort_order').order('name');
    if (error) toast.error('Could not load qualifications. Run the qualifications SQL in Supabase first (' + error.message + ')');
    setRows(data || []);
    const use = {};
    for (let from = 0; ; from += 1000) {
      const { data: st, error: se } = await supabase.from('students').select('academic_qualification').not('academic_qualification', 'is', null).order('id').range(from, from + 999);
      if (se) break;
      (st || []).forEach(x => { const k = qKey(x.academic_qualification); if (k) use[k] = (use[k] || 0) + 1; });
      if (!st || st.length < 1000) break;
    }
    setUsage(use);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function add() {
    const n = name.trim();
    if (!n) return;
    if (rows.some(r => qKey(r.name) === qKey(n))) { toast.error('"' + n + '" is already in the list'); return; }
    const next = rows.reduce((m, r) => Math.max(m, Number(r.sort_order) || 0), 0) + 1;
    const { error } = await supabase.from('qualifications').insert({ name: n, sort_order: next, is_active: true });
    if (error) { toast.error(error.message); return; }
    setName(''); toast.success('Qualification added'); load();
  }

  async function rename(r) {
    const n = editName.trim();
    if (!n || n === r.name) { setEditId(null); return; }
    if (rows.some(x => x.id !== r.id && qKey(x.name) === qKey(n))) { toast.error('"' + n + '" is already in the list'); return; }
    const cnt = usage[qKey(r.name)] || 0;
    if (cnt > 0 && !window.confirm('Rename "' + r.name + '" to "' + n + '"?\n\n' + cnt + ' student' + (cnt === 1 ? '' : 's') + ' with this qualification will be updated too.')) return;
    const { error } = await supabase.from('qualifications').update({ name: n }).eq('id', r.id);
    if (error) { toast.error(error.message); return; }
    if (cnt > 0) {
      const { error: ue } = await supabase.from('students').update({ academic_qualification: n }).eq('academic_qualification', r.name);
      if (ue) toast.error('Renamed in the list, but the students could not be updated: ' + ue.message);
    }
    setEditId(null); toast.success('Qualification updated'); load();
  }

  async function remove(r) {
    const cnt = usage[qKey(r.name)] || 0;
    if (!window.confirm('Delete "' + r.name + '" from the list?' + (cnt > 0 ? '\n\n' + cnt + ' student' + (cnt === 1 ? ' has' : 's have') + ' this qualification. Their records keep the value; it just will not be offered in the dropdown.' : ''))) return;
    const { error } = await supabase.from('qualifications').delete().eq('id', r.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Deleted'); load();
  }

  async function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const list = [...rows];
    [list[i], list[j]] = [list[j], list[i]];
    const ups = list.map((r, idx) => ({ r, order: idx + 1 })).filter(x => Number(x.r.sort_order) !== x.order);
    const res = await Promise.all(ups.map(x => supabase.from('qualifications').update({ sort_order: x.order }).eq('id', x.r.id)));
    const bad = res.find(x => x.error);
    if (bad) toast.error(bad.error.message);
    load();
  }

  const shown = rows.filter(r => !q || qKey(r.name).includes(qKey(q)));
  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>These are the values in the Qualification dropdown of the student profile and the Students filter. The order here is the order of the dropdown.</p>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} placeholder="New qualification e.g. B.Tech-Mechanical" style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={add}><Plus size={14} /> Add</button>
        </div>
      )}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." style={{ marginBottom: 10, maxWidth: 260 }} />
      {loading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div> : (
        <table>
          <thead><tr><th style={{ width: 40 }}>#</th><th>Qualification</th><th style={{ width: 120, textAlign: 'center' }}>Students</th><th style={{ width: 90, textAlign: 'center' }}>Order</th><th style={{ width: 110 }}>Actions</th></tr></thead>
          <tbody>
            {shown.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No qualifications found</p></div></td></tr> : shown.map(r => {
              const i = rows.findIndex(x => x.id === r.id);
              return (
                <tr key={r.id}>
                  <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>
                    {editId === r.id
                      ? <input autoFocus value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') rename(r); if (e.key === 'Escape') setEditId(null); }} style={{ width: '100%' }} />
                      : r.name}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>{usage[qKey(r.name)] || 0}</td>
                  <td style={{ textAlign: 'center' }}>
                    {canEdit && !q && <>
                      <button className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">▲</button>
                      <button className="btn btn-ghost btn-sm" disabled={i === rows.length - 1} onClick={() => move(i, 1)} title="Move down">▼</button>
                    </>}
                  </td>
                  <td>
                    {canEdit && (editId === r.id ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => rename(r)}>Save</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}><X size={13} /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(r.id); setEditName(r.name); }}><Edit2 size={13} /></button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => remove(r)}><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
